// ============================================================================
// FILE: src/modules/ppe/pages/masters/sku-attributes/SKUAttributePage.js
// ============================================================================

import React, { useEffect, useState } from "react";
import SKUAttributeForm from "./SKUAttributeForm";
import skuAttributeService from "./skuAttributeService";

const SKUAttributePage = () => {

  const [attributes, setAttributes] = useState([]);
  const [loading, setLoading] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedAttribute, setSelectedAttribute] = useState(null);

  const [entityFilter, setEntityFilter] = useState("RAW_MATERIAL");

  // ============================================================
  // FETCH ATTRIBUTES
  // ============================================================
  const fetchAttributes = async () => {
    try {
      setLoading(true);
      const res = await skuAttributeService.getAttributes(entityFilter);

      if (res.success) {
        setAttributes(res.data || []);
      }
    } catch (err) {
      console.error("Error fetching attributes", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAttributes();
  }, [entityFilter]);

  // ============================================================
  // HANDLERS
  // ============================================================
  const handleAdd = () => {
    setSelectedAttribute(null);
    setDrawerOpen(true);
  };

  const handleEdit = (attr) => {
    setSelectedAttribute(attr);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedAttribute(null);
  };

  const handleSaveSuccess = () => {
    handleCloseDrawer();
    fetchAttributes();
  };

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div className="sku-container">

      {/* HEADER */}
      <div className="sku-header">
        <div>
          <h2>SKU Attribute Master</h2>
          <p className="sku-subtitle">Manage dynamic attributes for materials and products</p>
        </div>

        <button className="btn-primary" onClick={handleAdd}>
          + Add Attribute
        </button>
      </div>

      {/* FILTER CARD */}
      <div className="sku-card filter-card">
        <label className="label">Entity</label>
        <select
          className="input"
          value={entityFilter}
          onChange={(e) => setEntityFilter(e.target.value)}
        >
          <option value="RAW_MATERIAL">Raw Material</option>
          <option value="BOUGHT_OUT">Bought Out</option>
          <option value="PRODUCT">Product</option>
        </select>
      </div>

      {/* TABLE CARD */}
      <div className="sku-card">

        {loading ? (
          <div className="empty-state">Loading...</div>
        ) : attributes.length === 0 ? (
          <div className="empty-state">No attributes found</div>
        ) : (
          <table className="sku-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Type</th>
                <th>Required</th>
                <th>Status</th>
                <th style={{ textAlign: "right" }}>Action</th>
              </tr>
            </thead>

            <tbody>
              {attributes.map((attr) => (
                <tr key={attr.id}>
                  <td className="code">{attr.attribute_code}</td>
                  <td>{attr.attribute_name}</td>
                  <td>
                    <span className="badge">{attr.data_type}</span>
                  </td>
                  <td>
                    {attr.is_required ? (
                      <span className="badge success">Yes</span>
                    ) : (
                      <span className="badge">No</span>
                    )}
                  </td>
                  <td>
                    {attr.is_active ? (
                      <span className="badge success">Active</span>
                    ) : (
                      <span className="badge danger">Inactive</span>
                    )}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <button
                      className="btn-secondary"
                      onClick={() => handleEdit(attr)}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

      </div>

      {/* DRAWER */}
      {drawerOpen && (
        <SKUAttributeForm
          open={drawerOpen}
          onClose={handleCloseDrawer}
          onSuccess={handleSaveSuccess}
          editData={selectedAttribute}
        />
      )}

      {/* STYLES */}
      <style>{`
        .sku-container {
          padding: 20px;
          background: #f9fafb;
          min-height: 100vh;
        }

        .sku-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .sku-subtitle {
          color: #6b7280;
          font-size: 13px;
          margin-top: 4px;
        }

        .sku-card {
          background: #ffffff;
          border-radius: 10px;
          padding: 16px;
          margin-bottom: 16px;
          box-shadow: 0 1px 4px rgba(0,0,0,0.08);
        }

        .filter-card {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .label {
          font-weight: 500;
        }

        .input {
          padding: 6px 10px;
          border-radius: 6px;
          border: 1px solid #d1d5db;
        }

        .sku-table {
          width: 100%;
          border-collapse: collapse;
        }

        .sku-table th {
          background: #f3f4f6;
          padding: 10px;
          text-align: left;
          font-weight: 600;
          font-size: 13px;
        }

        .sku-table td {
          padding: 10px;
          border-top: 1px solid #e5e7eb;
        }

        .code {
          font-weight: 600;
        }

        .badge {
          padding: 4px 8px;
          border-radius: 6px;
          font-size: 12px;
          background: #e5e7eb;
        }

        .badge.success {
          background: #d1fae5;
          color: #065f46;
        }

        .badge.danger {
          background: #fee2e2;
          color: #991b1b;
        }

        .btn-primary {
          background: #2563eb;
          color: white;
          border: none;
          padding: 8px 14px;
          border-radius: 6px;
          cursor: pointer;
        }

        .btn-primary:hover {
          background: #1d4ed8;
        }

        .btn-secondary {
          background: #e5e7eb;
          border: none;
          padding: 6px 12px;
          border-radius: 6px;
          cursor: pointer;
        }

        .btn-secondary:hover {
          background: #d1d5db;
        }

        .empty-state {
          text-align: center;
          padding: 30px;
          color: #6b7280;
        }
      `}</style>

    </div>
  );
};

export default SKUAttributePage;